import { Job, SearchResults } from "../../Shared/scriptts/interfaces";
import { getUserObject, setUserObject } from "../../Shared/scriptts/session/sessionmain";
import { params } from '../search-main';
import usaj_ga from '../../Shared/script/Analytics/usaj-analytics'

/**
 * Builds search results cards and adds them to the results element.
 * @param data The data returned from the search api
 * @returns void
 */
export function buildSearchResultsCards(data: SearchResults) {
    const userObject = getUserObject();
    const resultsElem = document.querySelector<HTMLElement>('#search-results');
    if (!resultsElem) {
        return;
    }

    resultsElem.innerHTML = '';
    data.Jobs?.forEach((job, i) => {
        const isSaved = job.DocumentID in userObject.SavedJobs ? true : false;
        let saveJobContent = `<button class="flex items-center cursor-pointer text-sm" data-save-job="${job.DocumentID}" data-save="${!isSaved}">
                <svg class="w-4 h-4 fill-warning-dark mr-1" role="presentation"><use xlink:href="/img/icons/sprite.svg#${isSaved ? "star" : "star_outline"}" /></svg>
                ${isSaved ? "Saved" : "Save job"}
            </button>`

        resultsElem.innerHTML += `<div class="border border-gray-lighter bg-white p-4">
            <div class="flex justify-between items-start">
                <h2 class="font-normal"><a href="/job/${job.DocumentID}" data-search-result="${i}" data-document-id="${job.DocumentID}" class="no-underline">${job.Title}</a></h2>
                ${saveJobContent}
            </div>
            <div class="w-full md:flex md:justify-between md:gap-x-2">
                <div class="basis-2/3">
                    <p><strong>${job.Agency}</strong></p><p>${job.Department}</p><p>${job.Location}</p>${(job.SubAgency) ? `<p>${job.SubAgency}</p>` : ''}
                </div>
                <div class="text-gray-dark basis-1/3">
                    <p>${job.SalaryDisplay}</p><p>${getWorkTypeDisplay(job)}</p>
                    <p class="text-sm flex flex-wrap items-center gap-x-2"><span class="md:hidden">This job open to:</span>${getHiringPathDisplay(job)}</p>
                </div>
            </div>
            <div class="mt-4 text-sm italic text-gray-dark flex flex-nowrap items-center gap-x-1">
                <svg class="w-4 h-4 fill-${getClockColor(job)}" role="presentation"><use xlink:href="/img/icons/sprite.svg#schedule" /></svg> ${job.DateDisplay}
            </div>
        </div>`;
    });
    document.querySelectorAll<HTMLElement>(`[data-search-result]`).forEach(elem => elem.addEventListener('click', evt => searchResultClicked(evt, data)));
    document.querySelectorAll<HTMLButtonElement>('[data-save-job]').forEach(elem => elem.addEventListener('click', () => saveJobClicked(elem)));
}

function searchResultClicked(evt: Event, results: SearchResults) {
    const elem = <HTMLElement>(evt.currentTarget);
    const index = elem.getAttribute('data-search-result') || '';
    const documentId = elem.getAttribute('data-document-id') || '';

    if (index === '' || documentId === '') {
        return;
    }

    const userObject = getUserObject();
    userObject.SearchResults = {
        Params: params,
        SelectedDocumentId: documentId,
        ConversionId: crypto.randomUUID(),
        Results: {
            Items: [],
            ResultCount: params.TotalResults || 0,
            ReturnUrl: window.location.href,
            PageSets: []
        }
    };
    
    userObject.SearchResults.Params.ResultsPerPage = results.Pager.ItemsPerPage;

    document.querySelectorAll<HTMLElement>('[data-search-result]').forEach(elem => {
        userObject.SearchResults.Results.Items.push(elem.getAttribute('data-document-id') || '');
    });
    userObject.SearchResults.Results.PageSets.push({
        Page: params.Page === undefined ? 1 : Number(params.Page),
        PageItems: userObject.SearchResults.Results.Items
    });
    
    setUserObject(userObject);
    usaj_ga.log_search_conversion_event(userObject, "search_conversion_click");
}

function saveJobClicked(elem: HTMLButtonElement) {
    const docId = Number(elem.getAttribute('data-save-job'));
    const save = elem.getAttribute('data-save');

    fetch(`/search/savejob/${docId}/${save}`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Fetch error: ${response.status}`);
            }
        })
        .then(() => {
            var userObject = getUserObject();
            if (save === "true") {
                elem.setAttribute('data-save', 'false');
                userObject.SavedJobs[docId] = true;
            }
            else {
                elem.setAttribute('data-save', 'true');
                delete userObject.SavedJobs[docId];
            }
            elem.innerHTML = `<svg class="w-4 h-4 fill-warning-dark mr-1" role="presentation"><use xlink:href="/img/icons/sprite.svg#${save === 'true' ? 'star' : 'star_outline'}" /></svg> ${save === 'true' ? 'Saved' : 'Save job'}`;
            setUserObject(userObject);
        })
        .catch(err => console.error(err));
}

function getWorkTypeDisplay(job: Job): string {
    if (job.WorkType) {
        if (job.WorkSchedule) {
            return `${job.WorkType} &bull; ${job.WorkSchedule}`;
        }
        else {
            return job.WorkType;
        }
    }
    else if (job.WorkSchedule) {
        return job.WorkSchedule;
    }
    return '';
}

function getHiringPathDisplay(job: Job): string {
    let hpDisplay = '';
    job.HiringPath.forEach(hp => hpDisplay += `<svg class="w-7 h-7" role="img" aria-label="${hp.Tooltip}"><title>${hp.Tooltip}</title><use xlink:href="/img/icons/hiringpaths.svg#${hp.Code}" /></svg>`);
    return hpDisplay;
}

function getClockColor(job: Job): string {
    switch (job.ClockDisplay) {
        case 'job-closing-in-7':
            return 'warning';
        case 'job-closing-in-2':
            return 'emergency';
        default:
            return 'gray-dark';
    }
}
